// The map marker input (R1294, player-reach.md §2.2, ui-map-interface.md §8).
//
// Two gestures reach one decision here. On a desktop, `alt` with a click
// on the map opens the marker menu at the cursor, and the same chord on an
// own marker removes it. On a phone, the pinned peek carries MARK, which
// opens the menu at the pressed point, or REMOVE over an own marker. This
// module decides which, so the two gestures cannot drift apart; the menu
// is `ui/markermenu.ts` and the wire is `net/connection.ts`.
//
// An own marker is the one on the pressed cell, or the nearest one whose
// cell centre is inside `pick_px` SCREEN px of the press, so the target
// keeps its size at every zoom. Nobody but the owner removes a marker
// (player-reach.md §2.7), so a press on another commander's marker opens
// the menu as a press on the water does.

import { CHART } from '../gen/presentation';
import { markers as S } from '../gen/strings';
import type { MapMarker } from '../gen/proto/aquaphobia';
import { ownMarkerAt } from '../render/map-markers';
import { cellAt, cellCenter } from '../world';

/** What the input reads of the state store. `GameState` satisfies it. */
export interface MarkerStore {
  readonly mapMarkers: readonly MapMarker[];
  readonly playerId: number;
}

/** The one intent the input sends itself: the remove. The menu places. */
export interface MarkerWire {
  sendMarkerRemove(id: number): void;
}

/** What the input opens. `MarkerMenu` satisfies it. */
export interface MarkerMenuOpener {
  open(px: number, py: number, cell: number): void;
}

export interface MarkerInputDeps {
  state: MarkerStore;
  wire: () => MarkerWire;
  menu: MarkerMenuOpener;
  /** CSS px to world px, without the shake. The point object is reused. */
  toWorld: (cx: number, cy: number, out: { x: number; y: number }) => { x: number; y: number };
  /** The camera scale: CSS px for one world px. */
  scale: () => number;
}

/** The one action a pinned peek offers: its label and what a press does. */
export interface PeekAction {
  label: string;
  run(): void;
}

export class MarkerInput {
  private readonly pt = { x: 0, y: 0 };

  constructor(private readonly d: MarkerInputDeps) {}

  /**
   * What a press at a screen point names: the viewer's own marker under it,
   * else the cell under it, which is −1 off the map. Both gestures read
   * this one answer.
   */
  private target(cx: number, cy: number): { own: MapMarker | null; cell: number } {
    const p = this.d.toWorld(cx, cy, this.pt);
    const cell = cellAt(p.x, p.y);
    const tol = CHART.pieces.map_markers.fig.pick_px / Math.max(1e-6, this.d.scale());
    const own = ownMarkerAt(
      this.d.state.mapMarkers, this.d.state.playerId, cell, p.x, p.y, tol, cellCenter,
    );
    return { own, cell };
  }

  /**
   * The alt+click (R1294): an own marker under the cursor goes, and any
   * other point of the map opens the menu. A press off the map does
   * nothing. It answers what it did, for the tests.
   */
  altClick(cx: number, cy: number): 'remove' | 'menu' | 'none' {
    const { own, cell } = this.target(cx, cy);
    if (own) {
      this.d.wire().sendMarkerRemove(own.id);
      return 'remove';
    }
    if (cell < 0) return 'none';
    this.d.menu.open(cx, cy, cell);
    return 'menu';
  }

  /**
   * Is the viewer's own marker under a screen point? A click or a tap on
   * it opens the peek with REMOVE, whatever stood before (R2034).
   */
  ownAt(cx: number, cy: number): boolean {
    return this.target(cx, cy).own !== null;
  }

  /**
   * What the pinned peek at a screen point offers (R134, R1294): REMOVE
   * over an own marker, else MARK. A point off the map offers nothing.
   */
  peek(cx: number, cy: number): PeekAction | null {
    const { own, cell } = this.target(cx, cy);
    if (own) {
      const id = own.id;
      return { label: S.remove, run: () => this.d.wire().sendMarkerRemove(id) };
    }
    if (cell < 0) return null;
    return { label: S.mark, run: () => this.d.menu.open(cx, cy, cell) };
  }
}
